import React from 'react';
import { MenuItem as MuiMenuItem } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  MenuTriggerButton,
  SimpleMenu,
} from '../../../../components/Menus/SimpleMenu';
import {
  SubmissionStatus,
  type SubmissionTableRow,
} from '../../../../api/submissions/submissions.api.types';
import { withShowable } from '../../../_global/lib/withShowable';
import {
  RequestQuoteSteps,
  UNKNOWN_PRODUCT,
} from '../../../agencies/quotes/PrimeX/constants';
import { usePersona } from '../../../../components/hooks/usePersona';
import { useAllow } from '../../../../components/hooks/useAllow';
import { useCanPerformActionOnSubmission } from '../../utils';
import { useSubmissionReadOnly } from '../../hooks/useSubmissionReadOnly';
import { Modal } from '../../../../components/modals/v2/helpers/v2.modal.helpers';
import DeclineSubmissionModal from './DeclineSubmissionModal';
import MarkAsLostSubmissionModal from './MarkAsLostSubmissionModal';
import ReopenSubmissionModal from './ReopenSubmissionModal';
import { MarkAsExcessModalConfig } from './MarkAsExcessModal';
import { CmailComposerModalConfig } from './CmailComposerModal';
import { recomputeSubmissionAssignments } from '../../../../api/submissions/submissions.api';
import { useReopenLostSubmission } from '../../hooks/useReopenLostSubmission';
import { FEATURE_FLAG_KEYS } from '../../../../config/growthbook';
import { useQueryClient } from '@tanstack/react-query';
import { queries } from '../../../../queries';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { ChangeSubmissionStatusModal } from '../../modules/ChangeSubmissionStatusModal';

const MenuItem = withShowable(MuiMenuItem);

type CowbellSubmissionsWorkspaceOptionsProps = {
  data: SubmissionTableRow;
};

const CowbellSubmissionsWorkspaceOptionsComponent = ({
  data,
}: CowbellSubmissionsWorkspaceOptionsProps) => {
  const persona = usePersona();
  const history = useHistory();

  const createQuoteRedirect = () => {
    history.push(
      `${persona.basePath}/submissions/quotes/${UNKNOWN_PRODUCT}/${data.id}/requestQuote/${RequestQuoteSteps.VERIFY_SUBMISSION_INFO}`
    );
  };

  const handleEmailRedirect = () => {
    history.push(`/admin/cmail`, { msgId: data.msgId });
  };

  const isDeclined = data.submissionStatus === SubmissionStatus.DECLINED;

  const canPerformAction = useCanPerformActionOnSubmission(data);
  const { locked } = useSubmissionReadOnly(data);
  const queryClient = useQueryClient();
  const handleReopenLostSubmission = useReopenLostSubmission(data.id);
  const notifications = useNotifications();
  const canManageSubmissions = useAllow('submissions:manage');
  const isRoot = useAllow(['root:root']);
  const [changingStatus, setChangingStatus] = React.useState(false);
  const isCancelRewriteEnhanced = useFeatureIsOn(
    FEATURE_FLAG_KEYS.CANCEL_REWRITE_ENHANCED_UI
  );
  const isPrimeOnePlusUsOn = useFeatureIsOn(
    FEATURE_FLAG_KEYS.PRIME_ONE_PLUS_US
  );

  const handleRefreshAssignments = async () => {
    try {
      await recomputeSubmissionAssignments(data.id);
      notifications.success(
        `Assignments refreshed for ${data.submissionNumber}.`
      );
      queryClient.invalidateQueries(queries.submissions.workspace._def);
      queryClient.invalidateQueries(queries.submissions.list._def);
    } catch (err) {
      console.error(err);
      notifications.error(
        `Unable to refresh assignments for ${data.submissionNumber}.`
      );
    }
  };

  const handleDeclineSubmission = () => {
    Modal.show(
      {
        DeclineSubmissionModal: {
          component: DeclineSubmissionModal,
          config: {
            title: 'Decline Submission',
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
      },
      {
        data: {
          submissionId: data.id,
          accountId: data.agencyAccount?.accountId || '',
          submissionNumber: data.submissionNumber,
          accountName: String(data.firmographics.name || ''),
        },
        close: () => Modal.hide(),
      }
    );
  };

  const handleMarkAsLost = () => {
    Modal.show(
      {
        MarkAsLostSubmissionModal: {
          component: MarkAsLostSubmissionModal,
          config: {
            title: 'Mark as Lost',
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
      },
      {
        data: {
          submissionId: data.id,
          accountId: data.agencyAccount?.accountId || '',
          submissionNumber: data.submissionNumber,
          accountName: String(data.firmographics.name || ''),
        },
        close: () => Modal.hide(),
      }
    );
  };

  const handleReopenSubmission = () => {
    Modal.show(
      {
        ReopenSubmissionModal: {
          component: ReopenSubmissionModal,
          config: {
            title: 'Re-Open Submission',
            maxWidth: 'sm',
            fullWidth: true,
          },
        },
      },
      {
        data: {
          submissionId: data.id,
          accountId: data.agencyAccount?.accountId || '',
          submissionNumber: data.submissionNumber,
          accountName: String(data.firmographics.name || ''),
        },
        close: () => Modal.hide(),
      }
    );
  };

  const handleMarkAsExcess = () => {
    Modal.show(MarkAsExcessModalConfig, {
      data: {
        submissionId: data.id,
        accountId: data.agencyAccount?.accountId || '',
        submissionNumber: data.submissionNumber,
        accountName: String(data.firmographics.name || ''),
      },
    });
  };

  const handleRequestQuote = () => {
    history.push(`/admin/submissions/summary/${data.id}/coverages`);
  };

  const handleReplyToBroker = () => {
    if (data.msgId) {
      Modal.show(CmailComposerModalConfig, {
        data: {
          msgId: data.msgId,
          submissionId: data.id,
        },
      });
    }
  };

  const status = data.submissionStatus;
  const showGoToEmail = data.submissionSource === 'EMAIL';
  const showReplyToBroker =
    data.submissionSource === 'EMAIL' &&
    Boolean(data.msgId) &&
    (status === SubmissionStatus.PROCESSED ||
      status === SubmissionStatus.DECLINED);
  const showRequestQuote = !locked && status === SubmissionStatus.INCOMPLETE;
  const showMarkAsExcess =
    !locked && status === SubmissionStatus.INCOMPLETE && !isPrimeOnePlusUsOn;
  const showDecline = !locked && status === SubmissionStatus.INCOMPLETE;
  const showMarkAsLost =
    !locked &&
    (status === SubmissionStatus.INCOMPLETE ||
      status === SubmissionStatus.PROCESSED);
  const showReopenReferral =
    !locked && status === SubmissionStatus.CARRIER_REFERRAL;
  const showReopenLost =
    !locked && isCancelRewriteEnhanced && status === SubmissionStatus.LOST;
  const showReviewSubmission = !locked && isDeclined;
  const hasMenuItems =
    showGoToEmail ||
    showReplyToBroker ||
    showRequestQuote ||
    showMarkAsExcess ||
    showDecline ||
    showMarkAsLost ||
    showReopenReferral ||
    showReopenLost ||
    showReviewSubmission ||
    canManageSubmissions ||
    isRoot;

  if (!canPerformAction || !hasMenuItems) {
    return null;
  }

  const menu = (
    <SimpleMenu
      id="cowbell-account-options"
      anchor={<MenuTriggerButton />}
      minWidth="19rem"
    >
      <MenuItem show={showGoToEmail} onClick={handleEmailRedirect}>
        Go to Email
      </MenuItem>

      <MenuItem
        show={showReplyToBroker}
        onClick={(event) => {
          event.stopPropagation();
          handleReplyToBroker();
        }}
      >
        Reply to Broker
      </MenuItem>

      <MenuItem show={showRequestQuote} onClick={createQuoteRedirect}>
        Request Quote
      </MenuItem>

      <MenuItem show={showMarkAsExcess} onClick={handleMarkAsExcess}>
        Mark as Excess
      </MenuItem>

      <MenuItem show={showDecline} onClick={handleDeclineSubmission}>
        Decline Submission
      </MenuItem>

      <MenuItem show={showMarkAsLost} onClick={handleMarkAsLost}>
        Mark as Lost
      </MenuItem>

      <MenuItem show={showReopenReferral} onClick={handleReopenSubmission}>
        Re-Open Submission
      </MenuItem>

      <MenuItem show={showReopenLost} onClick={handleReopenLostSubmission}>
        Re-Open Submission
      </MenuItem>

      <MenuItem show={showReviewSubmission} onClick={createQuoteRedirect}>
        Review Submission
      </MenuItem>

      <MenuItem
        show={canManageSubmissions}
        onClick={(event) => {
          event.stopPropagation();
          handleRefreshAssignments();
        }}
      >
        Refresh Assignments
      </MenuItem>

      <MenuItem
        show={isRoot}
        onClick={(event) => {
          event.stopPropagation();
          setChangingStatus(true);
        }}
      >
        Change Status
      </MenuItem>
    </SimpleMenu>
  );

  return (
    <>
      {menu}
      {changingStatus && (
        <ChangeSubmissionStatusModal
          submissionId={data.id}
          submissionNumber={data.submissionNumber}
          currentStatus={data.submissionStatus}
          onClose={() => setChangingStatus(false)}
        />
      )}
    </>
  );
};

export const CowbellSubmissionsWorkspaceOptions =
  CowbellSubmissionsWorkspaceOptionsComponent;
